import React from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Mail, 
  MapPin, 
  Instagram,
  Globe,
  MessageCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FooterEN = () => {
  const navigate = useNavigate();

  const quickLinks = [
    { name: 'Who we are', href: '#who-we-are' },
    { name: 'What we do', href: '#achievements' },
    { name: 'How you help', href: '#donate' }
  ];

  const legalLinks = [
    { name: 'Imprint', href: '/en/imprint' },
    { name: 'Privacy', href: '/en/privacy' },
    { name: 'Transparency', href: '/en/transparency' }
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/hayforhumanity?igsh=MXVjaHB3Ync4MjgxZA==', label: 'Instagram' },
    { icon: MessageCircle, href: 'https://whatsapp.com/channel/0029Vb7J4Wr8qIzrTYt6rl0O', label: 'WhatsApp' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const handleNavigation = (sectionId) => {
    if (window.location.pathname === '/en') {
      // If we're on the English homepage, just scroll
      scrollToSection(sectionId);
    } else {
      // If we're on another page, navigate to English homepage and then scroll
      navigate('/en');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    }
  };

  return (
    <footer className="bg-charcoal text-white relative">
      {/* Pre-Footer CTA - Danke für euer Vertrauen */}
      <div className="bg-gradient-to-r from-olive-green to-olive-dark py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="rounded-2xl p-12 text-center text-white">
              <div className="flex flex-col lg:flex-row items-center justify-between max-w-5xl mx-auto">
                <div className="lg:flex-1">
                  <h3 className="text-3xl font-bold mb-6 font-heading">🙌 Thank you for your trust!</h3>
                  <p className="text-xl leading-relaxed opacity-90 mb-8">
                    Thanks to everyone who has donated, shared, or shown compassion so far. 
                    Let us show together: <strong>Solidarity is stronger than resignation.</strong>
                  </p>
                  <div className="text-2xl font-bold mb-4">
                    hay - humanity. action. you. e.V.
                  </div>
                  <div className="text-xl">
                    Help that connects. Hope that remains.
                  </div>
                </div>
                <div className="lg:ml-12 mt-8 lg:mt-0">
                  <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6">
                    <img
                      src="/images/hay-logo-english.png"
                      alt="hay - humanity. action. you. Logo"
                      className="w-48 h-auto mx-auto opacity-90"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
            {/* Brand Section */}
            <motion.div
              className="lg:col-span-1 md:col-span-2"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex items-center space-x-3 mb-6 cursor-pointer group"
              >
                <div className="w-16 h-16 bg-olive-green rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Heart className="w-8 h-8 text-white fill-white" />
                </div>
                <div>
                  <h4 className="text-base font-bold font-heading">hay - humanity. action. you.</h4>
                  <p className="text-white/70 text-sm">Association for Humanitarian Aid & Solidarity.</p>
                </div>
              </button>
              <p className="text-white/80 leading-relaxed mb-6">
                An association of people with family roots in Gaza. 
                Help that connects. Hope that remains.
              </p>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 hover:bg-olive-green rounded-full flex items-center justify-center transition-all hover:scale-110"
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -2 }}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h5 className="text-xl font-bold mb-6 font-heading">Navigation</h5>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <button
                      onClick={() => handleNavigation(link.href.replace('#', ''))}
                      className="text-white/80 hover:text-olive-green transition-colors hover:translate-x-1 transform duration-200 inline-block"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h5 className="text-xl font-bold mb-6 font-heading">Contact & Exchange</h5>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-olive-green flex-shrink-0" />
                  <div className="text-white/80 text-sm">info@hayforhumanity.de</div>
                </div>
              </div>
            </motion.div>

            {/* Legal Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h5 className="text-xl font-bold mb-6 font-heading">Legal</h5>
              <ul className="space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      className="text-white/80 hover:text-olive-green transition-colors text-sm hover:translate-x-1 transform duration-200 inline-block"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/20 py-6">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-white/70 text-sm text-center md:text-left">
              © 2025 hay - humanity. action. you. e.V. All rights reserved.
            </div>
            <div className="flex items-center">
              <div className="text-white/70 text-sm">
                Vereinsregister: VR 5495
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterEN;